/**
 * NotificationDropdown Component (AC-540)
 *
 * Displays a dropdown list of notifications when the bell icon is clicked.
 * Shows loading state, empty state, error state, and notification list.
 *
 * Related Tickets:
 * - AC-532 (Epic: In-App Notification System)
 * - AC-540 (NotificationDropdown component - THIS TICKET)
 */

import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  InboxIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useNotificationStore } from '../../store/notificationStore';
import { Notification, NotificationStore } from '../../types/notification';
import NotificationItem from './NotificationItem';

// =============================================================================
// Component
// =============================================================================

const NotificationDropdown: React.FC = () => {
  // Get navigation function - always call hook unconditionally per Rules of Hooks
  const navigate = useNavigate();

  // Get store state and actions - destructure from single call for better test mock compatibility
  const {
    notifications,
    isLoading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    setDropdownOpen,
  } = useNotificationStore() as NotificationStore;

  // Check if there are any unread notifications
  const hasUnread = useMemo(
    () => notifications?.some((n: Notification) => !n.is_read) ?? false,
    [notifications]
  );

  // Sort notifications by created_at (most recent first)
  const sortedNotifications = useMemo(() => {
    return [...notifications].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [notifications]);

  // Handle notification click
  const handleNotificationClick = useCallback(
    async (notification: Notification) => {
      // Mark as read
      if (!notification.is_read) {
        await markAsRead(notification.notification_id);
      }

      // Navigate to action URL if provided
      if (notification.action_url) {
        navigate(notification.action_url);
      }

      // Close dropdown
      if (setDropdownOpen) {
        setDropdownOpen(false);
      }
    },
    [markAsRead, navigate, setDropdownOpen]
  );

  // Handle delete notification
  const handleDeleteNotification = useCallback(
    async (notificationId: number) => {
      await deleteNotification(notificationId);
    },
    [deleteNotification]
  );

  // Handle mark all as read
  const handleMarkAllAsRead = useCallback(async () => {
    await markAllAsRead();
  }, [markAllAsRead]);

  // Generate aria-label based on notification count
  const ariaLabel = useMemo(() => {
    if (!notifications || notifications.length === 0) {
      return 'Notifications - no new notifications';
    }
    const unreadCount = notifications.filter((n: Notification) => !n.is_read).length;
    if (unreadCount > 0) {
      return `Notifications - ${unreadCount} unread of ${notifications.length} notifications`;
    }
    return `Notifications - ${notifications.length} notifications`;
  }, [notifications]);

  return (
    <div
      data-testid="notification-dropdown"
      className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white dark:bg-cap-dark rounded-lg shadow-lg border border-gray-200 dark:border-gray-700"
      role="menu"
      aria-label={ariaLabel}
    >
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-cap-dark border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-warm-white">Notifications</h3>
        {hasUnread && (
          <button
            onClick={handleMarkAllAsRead}
            className="text-xs text-primary-600 hover:text-primary-800 font-medium"
            aria-label="Mark all notifications as read"
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div
          data-testid="loading-spinner"
          className="flex items-center justify-center py-8"
        >
          <ArrowPathIcon className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-100 dark:border-red-800">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && notifications.length === 0 && (
        <div
          data-testid="empty-state"
          className="flex flex-col items-center justify-center py-8 px-4"
        >
          <InboxIcon className="w-12 h-12 text-gray-300 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No notifications - you're all caught up!</p>
        </div>
      )}

      {/* Notification List */}
      {!isLoading && sortedNotifications.length > 0 && (
        <div className="divide-y divide-gray-100 dark:divide-gray-700" role="list">
          {sortedNotifications.map((notification) => (
            <NotificationItem
              key={notification.notification_id}
              notification={notification}
              onClick={() => handleNotificationClick(notification)}
              onDelete={() => handleDeleteNotification(notification.notification_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
